import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Account from "@/pages/Account";
import MyPacks from "@/pages/MyPacks";
import GuidesHub from "@/pages/guides/GuidesHub";
import GuidePage from "@/pages/guides/GuidePage";
import Faq from "@/pages/Faq";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Hub from "@/pages/hub/Hub";
import HubSection from "@/pages/hub/HubSection";
import HubReader from "@/pages/hub/HubReader";
import Legal from "@/pages/Legal";

function ScrollToTop({ lenisRef }) {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    // skip same-page anchor navigation and browser back/forward (POP)
    if (hash || navType === "POP") return;
    // Lenis owns the scroll position — window.scrollTo alone is overridden
    if (lenisRef?.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
  }, [pathname, hash, navType, lenisRef]);
  return null;
}

export default function App() {
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop lenisRef={lenisRef} />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/account" element={<Account />} />
              <Route path="/packs" element={<MyPacks />} />
              <Route path="/guides" element={<GuidesHub />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/hub" element={<Hub />} />
              <Route path="/hub/:sectionSlug" element={<HubSection />} />
              <Route path="/hub/:sectionSlug/:resourceId" element={<HubReader />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/refunds" element={<Legal kind="refunds" />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <Toaster theme="dark" position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
      </div>
    </MotionConfig>
  );
}
